import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaCrosshairs } from 'react-icons/fa';
import mapboxgl from 'mapbox-gl';
import { useHistoricalListings } from './services/historicalDataService';
import './HistoricalSplitView.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import Footer from './components/Footer/Footer'; // Import Footer
import { StyleConstants } from './StyleConstants';
import HistoricalListingsContainer from './components/HistoricalListings/HistoricalListingsContainer';

// Set Mapbox token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

// Function to determine if the banner should show "Listing" or "Buyside"
const getAgentBannerText = (rawData) => {
  if (!rawData) return null;
  
  const listingAgents = [
    rawData['Listing Agent'],
    rawData['Co-Listing Agent']
  ].filter(Boolean).map(agent => agent?.toLowerCase() || '');
  
  const sellingAgents = [
    rawData['Selling Agent'],
    rawData['Co-Selling Agent']
  ].filter(Boolean).map(agent => agent?.toLowerCase() || '');
  
  const isListingAgent = listingAgents.some(agent => 
    agent.includes('snippen')
  );
  
  const isSellingAgent = sellingAgents.some(agent => 
    agent.includes('snippen')
  );
  
  if (isListingAgent) return 'Listing';
  if (isSellingAgent) return 'Buyside';
  return null;
};

const HistoricalSplitView = ({ isMobile }) => {
  const [currentSplitMode, setCurrentSplitMode] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setCurrentSplitMode(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use the historical data service for error handling
  const { error: listingsError } = useHistoricalListings();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(listingsError || null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const initialBoundsFitted = useRef(false);
  const clickPopupRef = useRef(null);
  const popupPanTimeoutRef = useRef(null); // For click popups from listing cards
  const canClosePopupOnMapClickRef = useRef(true); // To manage deferring map click handler
  // Removed unused popupGracePeriodTimeoutRef as we now handle popups directly
  // Removed unused isMarkerClickRef as we now handle marker clicks directly in the marker event listener
  const activeMarkerElementRef = useRef(null); // Ref to the currently active marker's DOM element

  const [hoveredListingId, setHoveredListingId] = useState(null);
  const [clickedListingId, setClickedListingId] = useState(null);
  const [highlightedListingId, setHighlightedListingId] = useState(null);

  // Ref for the scrollable left list container
  const leftListScrollRef = useRef(null);

  // Always scroll and highlight left list to corresponding listing when a marker is clicked
  useEffect(() => {
    if (!clickedListingId) return;
    // Highlight the listing if not already
    if (highlightedListingId !== clickedListingId) {
      setHighlightedListingId(clickedListingId);
    }
    // Scroll to the listing
    const scrollContainer = leftListScrollRef.current;
    const listingElement = document.getElementById(`listing-item-${clickedListingId}`);
    if (listingElement && scrollContainer) {
      console.log('[SplitView] Scrolling listing into view (marker click):', clickedListingId);
      listingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [clickedListingId]);

  // Deselect highlight, popup, and marker state
  const deselectListing = () => {
    setHighlightedListingId(null);
    setClickedListingId(null);
    if (clickPopupRef.current) {
      clickPopupRef.current.remove();
      clickPopupRef.current = null;
    }
    if (activeMarkerElementRef.current) {
      const markerEl = activeMarkerElementRef.current;
      const svgElement = markerEl.querySelector('svg');
      const svgPath = svgElement?.querySelector('path');
      if (svgElement && svgPath) {
        markerEl.classList.remove('highlighted-marker');
        svgElement.classList.remove('marker-svg-scaled');
        svgPath.setAttribute('fill', '#003DA5');
        markerEl.style.zIndex = '1';
      }
      activeMarkerElementRef.current = null;
    }
  };

  // Deselect highlight if user clicks outside the list or popup
  useEffect(() => {
    function handleClickOutside(event) {
      const popupEl = document.querySelector('.map-popup');
      let clickedHighlighted = false;
      if (highlightedListingId) {
        const highlightedRow = document.getElementById(`listing-item-${highlightedListingId}`);
        if (highlightedRow && highlightedRow.contains(event.target)) {
          clickedHighlighted = true;
        }
      }
      if (!clickedHighlighted && (!popupEl || !popupEl.contains(event.target))) {
        deselectListing();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [highlightedListingId]);
  const clickedListingIdRef = useRef(clickedListingId); // Ref to keep track of clickedListingId for closures

  // Keep clickedListingIdRef.current in sync with clickedListingId state
  useEffect(() => {
    clickedListingIdRef.current = clickedListingId;
  }, [clickedListingId]);

  // Handle marker animation and color change on hover
  useEffect(() => {

    if (!mapRef.current || !mapRef.current.loaded() || !markersRef.current) {
      return;
    }

    markersRef.current.forEach(markerInstance => {
      const markerElement = markerInstance.getElement();
      if (!markerElement || !markerInstance._snippenData) return;

      const svgElement = markerElement.querySelector('svg');
      const svgPath = svgElement?.querySelector('path');
      if (!svgElement || !svgPath) return;

      const markerId = markerInstance._snippenData.id;
      const isCurrentlyClickedInState = markerId === clickedListingId;
      const isCurrentlyHovered = markerId === hoveredListingId; // Simplified: hover always shows if hovered

      if (isCurrentlyClickedInState) {
        // Click handler is responsible for initial styling. This effect ensures the ref is synced.
        // We also rely on this effect to style if clickedListingId is set by other means (e.g. list click).
        // For this diagnostic, we'll let the click handler be the source of truth for direct clicks.
        // However, to ensure list clicks still work, we need to apply styles here too.
        // Let's refine: if the activeMarkerElementRef already IS this markerElement, assume click handler did its job.
        if (activeMarkerElementRef.current !== markerElement) {
            markerElement.classList.add('highlighted-marker');
            svgElement.classList.add('marker-svg-scaled');
            svgPath.setAttribute('fill', '#A7111B'); // Active red
        }
        activeMarkerElementRef.current = markerElement; // Sync ref with state
        markerElement.style.zIndex = '20'; // Clicked marker, above idle/hovered but far below popup
      } else if (isCurrentlyHovered) {
        markerElement.classList.add('highlighted-marker'); // Or a different class for hover
        svgElement.classList.add('marker-svg-scaled');    // Or different scale
        svgPath.setAttribute('fill', '#A7111B'); // Or a different hover color (currently same as active)
        markerElement.style.zIndex = '15'; // Hovered marker, above idle but below clicked
      } else {
        markerElement.classList.remove('highlighted-marker');
        svgElement.classList.remove('marker-svg-scaled');
        svgPath.setAttribute('fill', '#003DA5'); // Default blue
        markerElement.style.zIndex = '1';   // Idle marker (maintaining default low)
        if (activeMarkerElementRef.current === markerElement) {
          activeMarkerElementRef.current = null; // State says it's not active, clear ref
        }
      }
    });

    // If clickedListingId is null (no marker selected in state), ensure activeMarkerElementRef is also null
    // and its pointed-to element (if any) is reset. This handles cases where state was cleared externally.
    if (!clickedListingId && activeMarkerElementRef.current) {
      const stillActiveElement = activeMarkerElementRef.current;
      const svgElement = stillActiveElement.querySelector('svg');
      const svgPath = svgElement?.querySelector('path');
      if (svgElement && svgPath) {
          stillActiveElement.classList.remove('highlighted-marker');
          svgElement.classList.remove('marker-svg-scaled');
          svgPath.setAttribute('fill', '#003DA5'); // Default blue
          if (stillActiveElement) stillActiveElement.style.zIndex = '1'; // Reset to default low
      }
      activeMarkerElementRef.current = null;
    }
  }, [clickedListingId, hoveredListingId, points, mapRef]); // `points` is crucial as markersRef.current depends on it.

  // Keep clickedListingIdRef updated for use in closures
  useEffect(() => {
    clickedListingIdRef.current = clickedListingId;
  }, [clickedListingId]);
  
  const handleListingItemSelect = useCallback((listingId, isMarkerClick = false) => {
    // For marker clicks, we now handle them directly in the marker click handler
    if (isMarkerClick) {
      return; // Marker clicks are handled separately in the marker click event
    }
    

    
    // Find the marker that corresponds to this listing
    const marker = markersRef.current?.find(m => 
      m._snippenData && m._snippenData.id === listingId
    );
    
    if (!marker) {
      console.error('[handleListingItemSelect] Could not find marker for listing ID:', listingId);
      return;
    }
    
    // Get the marker's DOM element
    const markerElement = marker.getElement();
    if (!markerElement) {
      console.error('[handleListingItemSelect] Could not get marker element for listing ID:', listingId);
      return;
    }

    // If a different popup is already open, close it immediately
    if (clickPopupRef.current && clickedListingIdRef.current && clickedListingIdRef.current !== listingId) {
      clickPopupRef.current.remove();
      clickPopupRef.current = null;
      setClickedListingId(null); // This will also reset the previous marker's style via useEffect
    }
    
    // Get the marker's coordinates
    const lngLat = marker.getLngLat();
    const point = new mapboxgl.LngLat(lngLat.lng, lngLat.lat);
    
    // Check if the point is in the current viewport
    const padding = 50; // pixels of padding around the viewport
    const container = mapRef.current.getContainer();
    const pointInPixels = mapRef.current.project(point);
    
    const triggerMarkerClick = () => {
      markerElement.dispatchEvent(new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      }));
    };

    const needsPan = pointInPixels.x < -padding || 
                    pointInPixels.x > container.clientWidth + padding ||
                    pointInPixels.y < -padding || 
                    pointInPixels.y > container.clientHeight + padding;
    
    const needsAdjustment = !needsPan && (
      pointInPixels.x < 100 || 
      pointInPixels.x > container.clientWidth - 100 ||
      pointInPixels.y < 100 || 
      pointInPixels.y > container.clientHeight - 100
    );

    // Clear any existing pan-related popup timeout
    if (popupPanTimeoutRef.current) {
      clearTimeout(popupPanTimeoutRef.current);
      popupPanTimeoutRef.current = null;
    }

    const flyToDuration = 1500; // Consistent with previous setting
    const easeToDuration = 500; // Consistent with previous setting
    const POPUP_TIMING_FACTOR = 0.55; // Show popup at 55% of pan duration

    if (needsPan) {
      popupPanTimeoutRef.current = setTimeout(() => {
        triggerMarkerClick();
        popupPanTimeoutRef.current = null; // Clear ref after execution
      }, flyToDuration * POPUP_TIMING_FACTOR);

      mapRef.current.flyTo({
        center: [lngLat.lng, lngLat.lat],
        essential: true,
        duration: flyToDuration,
        offset: [0, 0],
        speed: 1.0,
        curve: 1.2
      });
    } else if (needsAdjustment) {
      popupPanTimeoutRef.current = setTimeout(() => {
        triggerMarkerClick();
        popupPanTimeoutRef.current = null; // Clear ref after execution
      }, easeToDuration * POPUP_TIMING_FACTOR);

      mapRef.current.easeTo({
        center: [lngLat.lng, lngLat.lat],
        duration: easeToDuration
      });
    } else {
      triggerMarkerClick(); // No pan needed, show popup immediately
    }
    
    // Scroll to listing in the list if we have a corresponding element
    const listingElement = document.getElementById(`listing-item-${listingId}`);
    if (listingElement) {
      listingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [markersRef, mapRef]);

  // Handle clickedListingId state changes - only used for marker styling now, popup is managed directly
  useEffect(() => {

    if (!mapRef.current || !mapRef.current.loaded()) {
      return;
    }
    
    // We don't create popups here anymore - they're handled directly in marker click handler
    if (clickedListingId) {
      // Just make sure the marker click protection flag is set
      canClosePopupOnMapClickRef.current = false;
      setTimeout(() => {
        canClosePopupOnMapClickRef.current = true;
      }, 100);
    }
    
    // We only need to handle popup removal when clickedListingId becomes null
    // (indicating the popup should be closed)
    if (!clickedListingId && clickPopupRef.current) {
      clickPopupRef.current.remove();
      clickPopupRef.current = null;
    }
    
    // Always ensure map click handler is enabled when clickedListingId is null
    if (!clickedListingId) {
      canClosePopupOnMapClickRef.current = true;

    }
  }, [clickedListingId]); // Only depend on clickedListingId now



  // Load geocoded points (static data)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Using cached local variable to prevent unnecessary re-fetching
        const cachedData = window._cachedGeocodedData;
        
        if (cachedData) {

          setPoints(cachedData);
          setLoading(false);
          return;
        }
        
        const response = await fetch('/geocoded.json');
        if (!response.ok) throw new Error('Failed to load geocoded data');
        const data = await response.json();
        if (Array.isArray(data)) {
          // Cache the data in a window variable for future use
          window._cachedGeocodedData = data;
          setPoints(data);
        } else {
          console.error('Error loading geocoded data: Data is not an array.', data);
          setError('Failed to load map data: Invalid format');
          setPoints([]); // Ensure points is an array
        }
      } catch (err) {
        console.error('Error loading geocoded data:', err);
        setError('Failed to load map data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Define handleRecenterMap before it's used in any useEffect
  const handleRecenterMap = useCallback(() => {
    const map = mapRef.current;
    if (!map || !points || points.length === 0) {
      console.log('Recenter: No map or no points to recenter.');
      return;
    }

    // Scroll the left list to top
    if (leftListScrollRef.current) {
      leftListScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Close any open click popup before recentering
    if (clickPopupRef.current) {
      clickPopupRef.current.remove();
      clickPopupRef.current = null;
      setClickedListingId(null); // Also reset the ID that triggered this popup
    }

    // Reset any highlighted/active marker color to default blue
    if (activeMarkerElementRef.current) {
      const markerEl = activeMarkerElementRef.current;
      const svgElement = markerEl.querySelector('svg');
      const svgPath = svgElement?.querySelector('path');
      if (svgElement && svgPath) {
        markerEl.classList.remove('highlighted-marker');
        svgElement.classList.remove('marker-svg-scaled');
        svgPath.setAttribute('fill', '#003DA5'); // Default blue
        markerEl.style.zIndex = '1';
      }
      activeMarkerElementRef.current = null;
    }

    // Also clear the left-listing highlight state
    setHighlightedListingId(null); // Ensures list and map are visually reset


    const bounds = new mapboxgl.LngLatBounds();
    let validPointsExist = false;
    
    points.forEach(point => {
      // Check both point formats - direct lat/lon and geometry.coordinates
      if (point.lat && point.lon) {
        // Direct lat/lon format
        bounds.extend([parseFloat(point.lon), parseFloat(point.lat)]);
        validPointsExist = true;
      } else if (point.geometry?.coordinates) {
        // GeoJSON format [lon, lat]
        const [lon, lat] = point.geometry.coordinates;
        if (!isNaN(lon) && !isNaN(lat)) {
          bounds.extend([lon, lat]);
          validPointsExist = true;
        }
      }
    });

    if (validPointsExist) {
      const padding = { top: 50, bottom: 50, left: 60, right: 60 };
      map.fitBounds(bounds, {
        padding,
        maxZoom: 13,
        duration: 1000
      });
    } else {
      console.log('Recenter: No valid points found in historical data.');
    }
  }, [points, mapRef, clickPopupRef, setClickedListingId]);

  // Initialize map
  useEffect(() => {
    // Map initialization should run once after the container is available.
    if (!mapContainerRef.current) return;
    
    // Clean up any existing map instance first
    if (mapRef.current) {

      mapRef.current.remove();
      mapRef.current = null;
    }
    
    // Custom popup styles are now handled by HistoricalSplitView.css

    try {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [-121.3153, 44.0582], // Bend, OR (same as CurrentSplitView)
        zoom: 8, // Start even more zoomed out
        minZoom: 3,
        maxZoom: 18,
        attributionControl: true,
        preserveDrawingBuffer: true
      });
      
      mapRef.current = map;

      // Add navigation controls with proper positioning
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');

      // When map loads, fit bounds if we already have points
      map.on('load', () => {

        if (points && points.length > 0 && !initialBoundsFitted.current) {
          handleRecenterMap();
          initialBoundsFitted.current = true;
        }

        // Add a listener for when the map becomes idle (fully loaded)
        // This helps ensure markers appear promptly
        map.once('idle', () => {

          // Your existing marker code will handle this
        });
      });

      // Map click handler - closes active popup when clicking outside markers
      const handlePersistentMapClick = (e) => {
        // If no popup is active, do nothing
        if (!clickPopupRef.current) {
          return;
        }
        
        // Don't close the popup during the grace period after opening it
        if (!canClosePopupOnMapClickRef.current) {
          return;
        }

        // Check if the click originated from a marker or its children (we don't want to close popup when clicking on markers)
        let targetElement = e.originalEvent ? e.originalEvent.target : null;
        let isMarkerClick = false;
        
        // Walk up the DOM tree to check if we clicked on a marker
        while (targetElement && !isMarkerClick) {
          if (targetElement.classList && targetElement.classList.contains('mapboxgl-marker')) {
            isMarkerClick = true;
            break;
          }
          targetElement = targetElement.parentNode;
        }

        // If we clicked on a marker, don't close the popup
        if (isMarkerClick) {
          return;
        }
        
        // Otherwise, close the popup by setting clickedListingId to null
        // This will trigger our useEffect to remove the popup
        setClickedListingId(null);
      };

      map.on('click', handlePersistentMapClick);
      // Store a reference to the handler on the map instance for cleanup
      mapRef.current.persistentMapClickHandler = handlePersistentMapClick;

      // Cleanup on unmount
      return () => {
        if (mapRef.current) {
          if (mapRef.current.persistentMapClickHandler) {
            mapRef.current.off('click', mapRef.current.persistentMapClickHandler);

          }
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    } catch (e) {
      console.error('Error initializing map:', e);
      setError('Failed to initialize map');
    }
  }, [points, handleRecenterMap]); // Include points and handleRecenterMap as dependencies

  // Add markers to map when points or map changes
  useEffect(() => {
    if (!mapRef.current || loading) {
      return;
    }
    
    const map = mapRef.current;
    
    if (markersRef.current) {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    }

    if (points.length === 0) {
        initialBoundsFitted.current = false; 
        return; 
    }
    
    points.forEach(point => {
      if (!point.lat || !point.lon) return;
      
      const pointId = point.raw?.['List Number'] || point.id || point.raw?.id || 
                      point.raw?.['MLS #'] || point.raw?.['ML #'] || 
                      point.raw?.['MLS Number'] || point.raw?.['MLSNumber'] || 
                      (point.raw?.['Street Address'] && point.raw?.['City'] ? 
                      `${point.raw['Street Address']}-${point.raw['City']}` : 
                      `listing-${Math.random().toString(36).substring(2, 10)}`);
      
      const marker = new mapboxgl.Marker({ 
        color: '#003DA5',
        scale: 0.8
      })
        .setLngLat([parseFloat(point.lon), parseFloat(point.lat)])
        .addTo(map);
        
    const rawDataForMarker = point.raw && typeof point.raw === 'object' ? point.raw : {};
    marker._snippenData = { id: pointId, ...rawDataForMarker }; 

    const markerElement = marker.getElement();
    markerElement.dataset.pointId = pointId; // Store pointId on the DOM element
    markerElement.style.zIndex = '1'; // Default low z-index for all markers
    marker.pointId = pointId; // Ensure marker instance has pointId for styling useEffect
    markerElement.addEventListener('mouseenter', () => {
      setHoveredListingId(pointId);
    });
    markerElement.addEventListener('mouseleave', () => {
      setHoveredListingId(null);
    });
    markerElement.addEventListener('click', (e) => {
      e.stopPropagation();


      const currentMarkerDomElement = markerElement;

      // Always close any existing popup first
      if (clickPopupRef.current) {
        clickPopupRef.current.remove();
        clickPopupRef.current = null;
      }

      // Case 1: Clicking the *same* marker that is (or should be) active (toggle off)
      if (clickedListingIdRef.current === pointId) {

        const svgElem = currentMarkerDomElement.querySelector('svg');
        const pathElem = svgElem?.querySelector('path');
        if (svgElem && pathElem) {
          currentMarkerDomElement.classList.remove('highlighted-marker');
          svgElem.classList.remove('marker-svg-scaled');
          pathElem.setAttribute('fill', '#003DA5'); // Default blue
        }
        if (activeMarkerElementRef.current === currentMarkerDomElement) {
          activeMarkerElementRef.current = null;
        }
        setClickedListingId(null);
      } else {
        // Case 2: Clicking a *new* marker (or a marker that wasn't the one in clickedListingIdRef.current)

        // Reset style of the *previously* active marker (if one exists and is different)
        if (activeMarkerElementRef.current && activeMarkerElementRef.current !== currentMarkerDomElement) {
          const prevMarkerDom = activeMarkerElementRef.current;
          const prevSvg = prevMarkerDom.querySelector('svg');
          const prevPath = prevSvg?.querySelector('path');
          if (prevSvg && prevPath) {
            prevMarkerDom.classList.remove('highlighted-marker');
            prevSvg.classList.remove('marker-svg-scaled');
            prevPath.setAttribute('fill', '#003DA5'); // Default blue
          }
        }

        // Apply styling to the *newly* clicked marker
        const clickedSvg = currentMarkerDomElement.querySelector('svg');
        const clickedPath = clickedSvg?.querySelector('path');
        if (clickedSvg && clickedPath) {
          currentMarkerDomElement.classList.add('highlighted-marker');
          clickedSvg.classList.add('marker-svg-scaled');
          clickedPath.setAttribute('fill', '#A7111B'); // Active red

        }

        activeMarkerElementRef.current = currentMarkerDomElement;
        setClickedListingId(pointId); // This will trigger the main useEffect for style consistency

        // Create and show new popup
        const raw = point.raw || {};
        let neighborhood = raw['Neighborhood'] || raw['Subdivision'] || '';
        // If neighborhood is empty or N/A, fall back to city
        if (!neighborhood || neighborhood.trim().toUpperCase() === 'N/A') {
          neighborhood = raw['City'] || '';
        }
        const year = raw['Sale Date']?.split('-')[0] || raw['Close Date']?.split('-')[0] || raw['Year'] || ''; // Prioritize Sale/Close Date for Sold Year
        const photoUrl = raw['Photo URL'] || null;
        
        const placeholderPhoto = 'https://via.placeholder.com/200x150.png?text=No+Image';
        const currentPhotoUrl = photoUrl || placeholderPhoto;

        const agentRole = getAgentBannerText(raw); // Get the agent role (Listing/Buyside)
        
        const popupHtml = `
          <div class="map-popup" data-listing-id="${pointId}" style="width: 200px; background-color: #fff; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); overflow: hidden; cursor: pointer;">
            <img src="${currentPhotoUrl}" alt="${neighborhood || 'Property'}" class="popup-photo" data-listing-id="${pointId}" style="width: 100%; height: 110px; object-fit: cover; display: block; cursor: pointer;" />
            <div style="padding: 8px 10px;">
              ${neighborhood ? `<div style="font-weight: 600; font-size: 14px; color: #333; line-height: 1.3; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${neighborhood}</div>` : ''}
              ${agentRole ? `<div style="font-size: 12px; color: #555; font-weight: 600;">${agentRole}</div>` : ''}
              ${year ? `<div style="font-size: 12px; color: #555;">Sold: ${year}</div>` : ''}
            </div>
          </div>
        `;
        
        const popup = new mapboxgl.Popup({
          closeOnClick: false,
          closeButton: true,
          maxWidth: '300px',
          offset: {
            'top': [0, 15],
            'top-left': [0, 15],
            'top-right': [0, 15],
            'bottom': [0, -35],
            'bottom-left': [0, -35],
            'bottom-right': [0, -35],
            'left': [25, 0],
            'right': [-25, 0]
          }
          // Anchor is deliberately omitted to let Mapbox choose the best one based on the offset object and available space.
        })
        .setLngLat([parseFloat(point.lon), parseFloat(point.lat)])
        .setHTML(popupHtml)
        .addTo(mapRef.current);

        const popupElement = popup.getElement();
        if (popupElement) {
          popupElement.style.zIndex = '9999'; // Very high z-index for the popup
        }
        
        clickPopupRef.current = popup;
        
        popup.on('close', () => {

          if (clickPopupRef.current === popup) { 
            clickPopupRef.current = null;
            // Only nullify clickedListingId if the closed popup corresponds to the currently active listing ID
            if (clickedListingIdRef.current === pointId) {
              setClickedListingId(null); // This will trigger the main useEffect to reset styles
            }
          }
        });

        canClosePopupOnMapClickRef.current = false;
        setTimeout(() => {
          canClosePopupOnMapClickRef.current = true;
        }, 100);

        // Attach click handler to popup photo to scroll to corresponding listing
        if (popupElement) {
          const photo = popupElement.querySelector('.popup-photo');
          if (photo) {
            photo.addEventListener('click', () => {
              const id = photo.getAttribute('data-listing-id');
              const listingElement = document.getElementById(`listing-item-${id}`);
              if (listingElement) {
                listingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
              setHighlightedListingId(id);
            });
          }
        }
      } // Closes 'else'
    }); // Closes markerElement.addEventListener('click', ...)

    markersRef.current.push(marker);
    });

    // Fit bounds after markers are added (same as CurrentSplitView approach)
    if (mapRef.current && mapRef.current.loaded() && points.length > 0) {

      const bounds = new mapboxgl.LngLatBounds();
      let validPointsExist = false;
      
      // Use the same bounds calculation method as in handleRecenterMap
      points.forEach(point => {
        if (point.lat && point.lon) {
          bounds.extend([parseFloat(point.lon), parseFloat(point.lat)]);
          validPointsExist = true;
        } else if (point.geometry?.coordinates) {
          const [lon, lat] = point.geometry.coordinates;
          if (!isNaN(lon) && !isNaN(lat)) {
            bounds.extend([lon, lat]);
            validPointsExist = true;
          }
        }
      });
      
      if (validPointsExist) {
        // Use the same padding format as CurrentSplitView
        mapRef.current.fitBounds(bounds, { 
          padding: 60, // Single value padding, like CurrentSplitView
          maxZoom: 13,
          duration: 1000
        });
      }
    }
  }, [points, loading, handleRecenterMap, initialBoundsFitted]); // Added initialBoundsFitted to dependencies

  // Error display logic (ensure it's correctly placed)
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        backgroundColor: '#f8f9fa',
        color: '#dc3545',
        padding: '20px',
        textAlign: 'center'
      }}>
        {error}
      </div>
    );
  }

// Loading indicator removed, error handling remains
// if (loading) { ... }


  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 110px)', // Fine-tuned to perfectly accommodate attribution
      width: '100%',
      backgroundColor: '#f8f9fa',
      overflow: 'hidden'
    }}>
      {/* Left side - Listings */}
      {/* Conditionally render or style the listings container based on currentSplitMode */}
      {/* Left panel - Listings */}
      <div style={{
        width: currentSplitMode ? '50%' : '100%', // Full width if not split
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: currentSplitMode ? '1px solid #e0e0e0' : 'none',
        backgroundColor: '#fff',
        boxSizing: 'border-box',
        overflow: 'hidden' // Prevent double scrollbars if child has overflowY
      }}>
        <div
          ref={leftListScrollRef}
          style={{ // Scrollable content wrapper
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: `0 ${StyleConstants.contentSidePadding} ${StyleConstants.contentBottomPadding} ${StyleConstants.contentSidePadding}`,
          }}
        >
          <HistoricalListingsContainer 
            listings={points.map(p => (p.raw && typeof p.raw === 'object' ? p.raw : {}))} 
            isSplitMode={currentSplitMode} 
            onListingHover={(listingId) => setHoveredListingId(listingId)}
            onListingClick={(listingId) => {
              if (highlightedListingId === listingId) {
                deselectListing();
              } else {
                handleListingItemSelect(listingId);
                setHighlightedListingId(listingId);
              }
            }} 
            highlightedListingId={highlightedListingId}
          />
        </div>
        {!isMobile && <Footer />}
      </div>
      
      {/* Right panel - Map - Conditionally render or style based on currentSplitMode */}
      {currentSplitMode && (
        <div style={{
          width: '50%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          <div 
            ref={mapContainerRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,
              overflow: 'hidden'
            }}
          >
            <button
              onClick={e => {
                handleRecenterMap();
                // Remove focus to prevent persistent highlight
                if (e.currentTarget) e.currentTarget.blur();
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 2, // Ensure it's above map tiles and other controls
                background: 'white',
                border: '1px solid #dbdbdb',
                borderRadius: '4px',
                padding: '8px',
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1'
              }}
              title="Recenter Map"
            >
              <FaCrosshairs size={18} color="#333" />
            </button>
            {error && (
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(220, 53, 69, 0.9)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                zIndex: 10,
                maxWidth: '80%',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
export default HistoricalSplitView;
